import { useState, useEffect } from 'react';
import { useAuth, PlanType } from '../contexts/AuthContext';
import { subscriptionService, Plan } from '../services/subscription';
import { toast } from '@/components/ui/sonner';

export function useSubscription() {
  const { user, userPlan, checkSubscriptionStatus: authCheckSubscription } = useAuth();
  const [status, setStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [isResuming, setIsResuming] = useState<boolean>(false);
  
  // Function to check subscription status
  const checkSubscriptionStatus = async () => {
    try {
      setIsLoading(true);
      const result = await subscriptionService.getStatus();
      setStatus(result);
      await authCheckSubscription();
      return result;
    } catch (err) {
      setError(err as Error);
      console.error("Subscription error:", err);
      toast("Failed to load subscription status", {
        className: "bg-red-50 text-red-600 border-red-200",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch subscription status when the component mounts or user changes
  useEffect(() => {
    checkSubscriptionStatus();
  }, [user, userPlan]);
  
  const upgrade = async (planId: string) => {
    if (!user) {
      toast("Please log in to upgrade your subscription", {
        className: "bg-red-50 text-red-600 border-red-200",
      });
      return;
    }
    
    try {
      setIsUpgrading(true);
      const result = await subscriptionService.upgrade(planId);
      
      // In a real implementation, you would redirect to Stripe checkout
      // window.location.href = result.checkout_url;
      
      // For demonstration, we'll update the plan locally
      await authCheckSubscription();
      
      const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
      toast(`Successfully upgraded to ${planName} plan.`, {
        className: "bg-green-50 text-green-600 border-green-200",
      });
      
      // Refresh subscription status
      await checkSubscriptionStatus();
      
    } catch (err) {
      console.error('Upgrade error:', err);
      toast("Failed to upgrade subscription", {
        className: "bg-red-50 text-red-600 border-red-200",
      });
    } finally {
      setIsUpgrading(false);
    }
  };
  
  const cancel = async () => {
    if (!user) return;
    
    try {
      setIsCancelling(true);
      const result = await subscriptionService.cancel();
      
      toast("Subscription cancelled. You can still use premium features until the end of your billing period.");
      
      // Update auth context with new plan
      await authCheckSubscription();
      
      // Refresh subscription status
      await checkSubscriptionStatus();
      
    } catch (err) {
      console.error('Cancel error:', err);
      toast("Failed to cancel subscription", {
        className: "bg-red-50 text-red-600 border-red-200",
      });
    } finally {
      setIsCancelling(false);
    }
  };
  
  const resume = async () => {
    if (!user) return;
    
    try {
      setIsResuming(true);
      const result = await subscriptionService.resume();
      
      toast("Subscription resumed successfully.");
      
      // Update auth context with new plan
      await authCheckSubscription();
      
      // Refresh subscription status
      await checkSubscriptionStatus();
      
    } catch (err) {
      console.error('Resume error:', err);
      toast("Failed to resume subscription", {
        className: "bg-red-50 text-red-600 border-red-200",
      });
    } finally {
      setIsResuming(false);
    }
  };
  
  const canAccessFeature = (feature: string): boolean => {
    if (!status) return false;
    
    // For demo purposes, define feature access based on plan
    const featureAccess: Record<string, PlanType[]> = {
      'gallery_images': ['premium'],
      'service_listings': ['free', 'premium'],
      'phone_number': ['premium'],
      'analytics': ['premium'],
      'detailed_analytics': ['premium'],
    };
    
    return featureAccess[feature]?.includes(userPlan) || false;
  };
  
  const getFeatureLimit = (feature: string): number | null => {
    if (!status) return null;
    return status.feature_limits[feature];
  };
  
  return {
    status,
    isLoading,
    error,
    upgrade,
    cancel,
    resume,
    canAccessFeature,
    getFeatureLimit,
    isUpgrading,
    isCancelling,
    isResuming,
    checkSubscriptionStatus,
  };
}
